/**
 * TimeProvider service - access to the current time (in DEV/STAGE optionally a mock time from the backend).
 * The backend API /api/dev/time is used in development and on stage.
 */

import { api } from '../api/api';

export interface TimeInfo {
  mockTime: string;
  mockTimeIso: string;
  realTime: string;
  realTimeIso: string;
  offsetMillis: number | null;
  isMocked: boolean;
  localDateTime: string;
  /** Server time in locale format (e.g. dd.MM.yyyy HH:mm) GMT+1 */
  localDateTimeServer?: string;
  realTimeServer?: string;
  serverZone?: string;
  serverZoneOffset?: string;
}

const DEV_TIME_BASE = '/api/dev/time';

class TimeProviderService {
  private isDev: boolean;
  private cachedTime: Date | null = null;
  private cacheTimeout = 1000;
  private lastFetch = 0;

  constructor() {
    const mode = import.meta.env.MODE ?? '';
    this.isDev =
      import.meta.env.DEV === true ||
      mode === 'development' ||
      mode === 'stage';
  }

  /**
   * Current time (in DEV: the mock time from the backend, when one is set).
   */
  async getCurrentTime(): Promise<Date> {
    if (!this.isDev) {
      return new Date();
    }

    const now = Date.now();
    if (this.cachedTime != null && now - this.lastFetch < this.cacheTimeout) {
      return new Date(this.cachedTime.getTime() + (now - this.lastFetch));
    }

    try {
      const { data } = await api.get<TimeInfo>(DEV_TIME_BASE);
      const mockTime = new Date(data.mockTime);
      this.cachedTime = mockTime;
      this.lastFetch = now;
      return mockTime;
    } catch {
      return new Date();
    }
  }

  /**
   * Current time, synchronously (from the cache or the system clock).
   */
  getCurrentTimeSync(): Date {
    if (!this.isDev || this.cachedTime == null) {
      return new Date();
    }
    const now = Date.now();
    return new Date(this.cachedTime.getTime() + (now - this.lastFetch));
  }

  /**
   * Sets the mock time (DEV only). time: an ISO-8601 string, or "now" to reset it.
   * The backend returns a simplified response; call getTimeInfo() afterwards for the full TimeInfo.
   */
  async setMockTime(time: Date | string): Promise<TimeInfo> {
    if (!this.isDev) {
      throw new Error('Mock time can only be set in dev/stage environment');
    }
    const timeString = typeof time === 'string' ? time : time.toISOString();
    await api.post(DEV_TIME_BASE, null, { params: { time: timeString } });
    this.cachedTime = null;
    return (await this.getTimeInfo()) ?? this.getFallbackTimeInfo();
  }

  /**
   * Resets the mock time to the system time (DEV only).
   */
  async resetMockTime(): Promise<TimeInfo> {
    if (!this.isDev) {
      throw new Error('Mock time can only be reset in dev/stage environment');
    }
    await api.delete(DEV_TIME_BASE);
    this.cachedTime = null;
    return (await this.getTimeInfo()) ?? this.getFallbackTimeInfo();
  }

  /**
   * Advances the mock time by a duration (DEV only).
   */
  async advanceTime(
    hours: number = 0,
    minutes: number = 0,
    seconds: number = 0
  ): Promise<TimeInfo> {
    if (!this.isDev) {
      throw new Error('Time can only be advanced in dev/stage environment');
    }
    await api.post(`${DEV_TIME_BASE}/advance`, null, {
      params: { hours, minutes, seconds },
    });
    this.cachedTime = null;
    return (await this.getTimeInfo()) ?? this.getFallbackTimeInfo();
  }

  private getFallbackTimeInfo(): TimeInfo {
    const now = new Date();
    const s = now.toISOString();
    return {
      mockTime: s,
      mockTimeIso: s,
      realTime: s,
      realTimeIso: s,
      offsetMillis: null,
      isMocked: false,
      localDateTime: s,
    };
  }

  /**
   * Current time information from the backend (DEV only, e.g. mockTime, realTime, isMocked).
   */
  async getTimeInfo(): Promise<TimeInfo | null> {
    if (!this.isDev) {
      return null;
    }
    try {
      const { data } = await api.get<TimeInfo>(DEV_TIME_BASE);
      return data;
    } catch {
      return null;
    }
  }
}

export const timeProvider = new TimeProviderService();
